// Element-style clipboard (format painter). Not Redux on purpose: must stay out
// of undo history and the saved training.
import { deriveTextFormatFromHtml } from "../../common/common_tools";

export const STYLE_CLIPBOARD_EVENT = "aidea:style-clipboard";

const TEXT_PLUGINS = new Set(["BasicText", "RichText"]);

export function isTextPlugin(pluginId) {
  return TEXT_PLUGINS.has(pluginId);
}

export function sourceKindOf(pluginId) {
  if (isTextPlugin(pluginId)) return "text";
  if (pluginId === "HotspotImages") return "image";
  if (pluginId === "EnrichedPlayer") return "video";
  return "other";
}

function num(value, fallback) {
  return value === undefined || value === null ? fallback : value;
}

// Unset source values become editor defaults so a paste also resets what the
// source lacks; `null` = "no explicit value", which applying turns into a clear.
function normalizeStyle(style) {
  const s = style || {};
  const pad = num(s.padding, 0);
  const radius = num(s.borderRadius, 0);
  return {
    padding: pad,
    paddingTop: num(s.paddingTop, pad),
    paddingRight: num(s.paddingRight, pad),
    paddingBottom: num(s.paddingBottom, pad),
    paddingLeft: num(s.paddingLeft, pad),
    backgroundColor: num(s.backgroundColor, "rgba(255,255,255,0)"),
    borderWidth: num(s.borderWidth, 0),
    borderStyle: num(s.borderStyle, "solid"),
    borderColor: num(s.borderColor, "#000000"),
    borderRadius: radius,
    borderTopLeftRadius: num(s.borderTopLeftRadius, radius),
    borderTopRightRadius: num(s.borderTopRightRadius, radius),
    borderBottomLeftRadius: num(s.borderBottomLeftRadius, radius),
    borderBottomRightRadius: num(s.borderBottomRightRadius, radius),
    opacity: num(s.opacity, 1),
    filter: num(s.filter, ""),
    verticalAlign: num(s.verticalAlign, "top"),
  };
}

// Box state first, then what the stored HTML actually renders with.
function normalizeTextFormat(state) {
  const st = state || {};
  const html = deriveTextFormatFromHtml(st.__text);
  return {
    bold: st.bold === undefined ? html.bold : !!st.bold,
    italic: st.italic === undefined ? html.italic : !!st.italic,
    underline: st.underline === undefined ? html.underline : !!st.underline,
    textAlign: num(st.textAlign, html.textAlign),
    textFontFamily: num(st.textFontFamily, html.textFontFamily),
    textFontSize: num(st.textFontSize, html.textFontSize),
    textColor: num(st.textColor, html.textColor),
    highlightColor: num(st.highlightColor, html.highlightColor),
  };
}

function normalizeTransform(structure) {
  const st = structure || {};
  return {
    rotation: num(st.rotation, 0),
    horizontalFlip: !!st.horizontalFlip,
    verticalFlip: !!st.verticalFlip,
  };
}

function normalizeImageAdjust(structure) {
  const st = structure || {};
  return {
    imageAdjust: num(st.imageAdjust, null),
    filterPreset: num(st.filterPreset, "none"),
    filterIntensity: num(st.filterIntensity, null),
  };
}

// Everything that counts as "look"; content, size, position, stacking and
// timeline timing stay out.
export function buildStylePayload(box, toolbar) {
  if (!box || !toolbar) return null;
  const isText = isTextPlugin(toolbar.pluginId);
  const sourceKind = sourceKindOf(toolbar.pluginId);
  return {
    sourceId: box.id,
    pluginId: toolbar.pluginId,
    isText,
    sourceKind,
    sourceUrl:
      (sourceKind === "image" || sourceKind === "video") && toolbar.state
        ? toolbar.state.url || null
        : null,
    style: normalizeStyle(toolbar.style),
    textFormat: isText ? normalizeTextFormat(toolbar.state) : null,
    transform: normalizeTransform(toolbar.structure),
    imageAdjust: normalizeImageAdjust(toolbar.structure),
    hasImageAdjust:
      !!(toolbar.structure || {}).imageAdjust ||
      ((toolbar.structure || {}).filterPreset || "none") !== "none",
    hasTransform:
      !!(toolbar.structure || {}).rotation ||
      !!(toolbar.structure || {}).horizontalFlip ||
      !!(toolbar.structure || {}).verticalFlip,
    entryAnimation: box.boxAnimation || box.textAnimation || null,
    exitAnimation: box.boxExitAnimation || null,
    copiedAt: Date.now(),
  };
}

// Same kind as the source, source excluded.
export function eligibleStyleTargets(payload, ids, pluginToolbarsById) {
  if (!payload || !Array.isArray(ids)) return [];
  return ids.filter((id) => {
    if (id === payload.sourceId) return false;
    const toolbar = pluginToolbarsById && pluginToolbarsById[id];
    return !!toolbar && isTextPlugin(toolbar.pluginId) === payload.isText;
  });
}

let clipboard = null;

function emit() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(STYLE_CLIPBOARD_EVENT, { detail: clipboard }));
  } catch (err) {
    console.warn("[styleClipboard] dispatch failed", err);
  }
}

export function copyStyle(payload) {
  clipboard = payload || null;
  emit();
  return clipboard;
}

export function getStyleClipboard() {
  return clipboard;
}

export function clearStyleClipboard() {
  clipboard = null;
  emit();
}

export function subscribeStyleClipboard(listener) {
  if (typeof window === "undefined") return () => {};
  const handler = (e) => listener(e.detail || null);
  window.addEventListener(STYLE_CLIPBOARD_EVENT, handler);
  return () => window.removeEventListener(STYLE_CLIPBOARD_EVENT, handler);
}
